import { useParams, useNavigate } from 'react-router-dom';
import { communities } from '@/mocks/communities';
import { useSEO } from '@/hooks/useSEO';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import SiteHeader from '@/components/feature/SiteHeader';
import CommunityHero from './components/CommunityHero';
import CommunityAbout from './components/CommunityAbout';
import CommunityReviews from './components/CommunityReviews';
import CommunityFAQ from './components/CommunityFAQ';
import RelatedCommunities from './components/RelatedCommunities';
import DetailSidebar from './components/DetailSidebar';
import Footer from '@/pages/home/components/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import GlowButton from '@/components/base/GlowButton';

export default function CommunityDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const community = communities.find((c) => c.id === id);

  const faqSchema = community
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `How do I join the ${community.name} Telegram community?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `You can join ${community.name} directly on Telegram by clicking the "Join Community" button on this page, or by visiting ${community.telegramLink}. The community is open to anyone interested in ${community.category.toLowerCase()} topics.`,
            },
          },
          {
            '@type': 'Question',
            name: `How many members does ${community.name} have?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${community.name} currently has ${community.memberCount.toLocaleString()} members, making it one of the ${community.activityLevel === 'high' ? 'most active' : 'notable'} Telegram communities in the ${community.category} category.`,
            },
          },
          {
            '@type': 'Question',
            name: `What topics does ${community.name} cover?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${community.name} focuses on ${community.tags.join(', ')} and related topics. ${community.description}`,
            },
          },
          {
            '@type': 'Question',
            name: `How active is ${community.name}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${community.name} has a ${community.activityLevel} activity level${community.weeklyMessages ? `, with approximately ${community.weeklyMessages.toLocaleString()} messages exchanged per week` : ''}. The community was established on ${new Date(community.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.`,
            },
          },
          {
            '@type': 'Question',
            name: `What language is used in ${community.name}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${community.name} primarily communicates in ${community.language ?? 'English'}. The community is based in the ${community.region} region and welcomes members from around the world.`,
            },
          },
          {
            '@type': 'Question',
            name: `Is ${community.name} free to join?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `Yes, ${community.name} is a free Telegram community. Simply click the join link and you will be able to participate in all discussions and access community resources immediately.`,
            },
          },
        ],
      }
    : null;

  useSEO({
    title: community
      ? `${community.name} – Telegram Community | TGDirectory`
      : 'Community Not Found | TGDirectory',
    description: community
      ? `Join ${community.name} on Telegram – ${community.memberCount.toLocaleString()} members, ${community.activityLevel} activity. ${community.description.slice(0, 120)}`
      : 'This Telegram community could not be found on TGDirectory.',
    keywords: community
      ? `${community.name}, ${community.category} Telegram group, ${community.tags.join(', ')}, Telegram community`
      : undefined,
    canonical: community ? `https://tgdirectory.app/community/${community.id}` : undefined,
    ogType: 'article',
    ogImage: community?.coverImage,
    schema: community
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${community.name} – Telegram Community`,
            url: `https://tgdirectory.app/community/${community.id}`,
            description: community.description,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://tgdirectory.app/' },
                { '@type': 'ListItem', position: 2, name: 'Community Directory', item: 'https://tgdirectory.app/' },
                { '@type': 'ListItem', position: 3, name: community.name, item: `https://tgdirectory.app/community/${community.id}` },
              ],
            },
          },
          ...(faqSchema ? [faqSchema] : []),
        ]
      : undefined,
  });

  const heroRef = useScrollReveal<HTMLDivElement>({ delay: 0, threshold: 0.05 });
  const aboutRef = useScrollReveal<HTMLDivElement>({ delay: 0, threshold: 0.08 });
  const reviewsRef = useScrollReveal<HTMLDivElement>({ delay: 0, threshold: 0.05 });
  const faqRef = useScrollReveal<HTMLDivElement>({ delay: 0, threshold: 0.05 });
  const relatedRef = useScrollReveal<HTMLDivElement>({ delay: 0, threshold: 0.05 });
  const adRef = useScrollReveal<HTMLDivElement>({ delay: 0, threshold: 0.08 });
  const sidebarRef = useScrollReveal<HTMLDivElement>({ delay: 120, threshold: 0.05 });

  if (!community) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] flex flex-col items-center justify-center text-slate-600">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0088CC]/10 mb-4">
          <i className="ri-search-line text-2xl text-[#0088CC]"></i>
        </div>
        <h2 className="text-xl font-bold text-slate-700 mb-2">Community Not Found</h2>
        <p className="text-slate-400 text-sm mb-6">This community doesn&apos;t exist or has been removed.</p>
        <GlowButton onClick={() => navigate('/')} className="text-sm font-semibold px-5 py-2.5 rounded-xl">
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          Back to Directory
        </GlowButton>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <SiteHeader showBack />

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-5">
          <button
            onClick={() => navigate('/')}
            className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Home
          </button>
          <span>/</span>
          <button
            onClick={() => navigate('/')}
            className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Community Directory
          </button>
          <span>/</span>
          <span className="text-[#0088CC] truncate max-w-[200px]">{community.name}</span>
        </nav>

        <div className="flex gap-6 items-start">
          {/* Main Content */}
          <div className="flex-1 min-w-0 flex flex-col gap-5">
            <div ref={heroRef}><CommunityHero community={community} /></div>
            <div ref={aboutRef}><CommunityAbout community={community} /></div>
            <div ref={reviewsRef}><CommunityReviews communityId={community.id} /></div>
            <div ref={faqRef}><CommunityFAQ community={community} /></div>
            <div ref={relatedRef}><RelatedCommunities current={community} all={communities} /></div>

            {/* Bottom Ad Banner */}
            <div ref={adRef} className="w-full rounded-2xl border border-dashed border-[#0088CC]/25 bg-white/60 backdrop-blur-md overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100">
                <span className="text-[10px] font-semibold text-[#0088CC] uppercase tracking-wider">Advertisement</span>
              </div>
              <div className="relative w-full h-24 md:h-28">
                <img
                  src="https://readdy.ai/api/search-image?query=minimalist%20frosted%20glass%20abstract%20soft%20pale%20blue%20white%20mist%20translucent%20wide%20horizontal%20layered%20wave%20geometric%20shapes%20subtle%20gradient%20clean%20light%20background%20elegant%20modern%20art%20banner%20panel&width=1200&height=112&seq=cp203&orientation=landscape"
                  alt="Advertisement"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-transparent to-white/85 flex items-center justify-between px-8">
                  <div>
                    <p className="text-slate-800 font-bold text-base">Advertise Your Community</p>
                    <p className="text-slate-500 text-sm">Reach 100,000+ Telegram users daily</p>
                  </div>
                  <GlowButton as="a" href="#contact" className="text-sm font-semibold px-5 py-2.5 rounded-full">
                    Get Started
                  </GlowButton>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div ref={sidebarRef} className="hidden xl:block w-72 shrink-0 sticky top-20">
            <DetailSidebar community={community} />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
